import { useState, useRef, useEffect } from "react";
import { Building2, Check, ChevronDown, Settings as SettingsIcon } from "lucide-react";
import { useOrg } from "../hooks/useOrg";

export function OrgSwitcher() {
  const { orgs, activeOrg, activeOrgId, switchOrg, redirectToOrgPage, isActiveOrgAdmin } = useOrg();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown on outside click
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  if (orgs.length === 0) {
    return null;
  }

  return (
    <div className="relative w-full text-left" ref={dropdownRef}>
      <button
        type="button"
        onClick={() => setIsOpen((prev) => !prev)}
        className="w-full flex items-center justify-between gap-2 px-2.5 py-1.5 rounded-lg border border-stone-200 dark:border-stone-800 bg-white dark:bg-stone-900 hover:bg-stone-50 dark:hover:bg-stone-800 text-xs font-medium text-stone-700 dark:text-stone-300 shadow-2xs transition"
        aria-expanded={isOpen}
      >
        <div className="flex items-center gap-2 min-w-0">
          <Building2 className="w-3.5 h-3.5 text-stone-500 dark:text-stone-400 shrink-0" />
          <span className="truncate">{activeOrg?.orgName || "Select Org"}</span>
        </div>
        <ChevronDown className="w-3 h-3 text-stone-400 dark:text-stone-500 shrink-0" />
      </button>

      {isOpen && (
        <div className="absolute left-0 bottom-full mb-1.5 w-64 origin-bottom-left rounded-xl bg-white dark:bg-stone-900 shadow-lg ring-1 ring-black/5 dark:ring-white/10 border border-stone-200 dark:border-stone-800 py-1.5 z-50">
          <div className="px-3 py-1.5 border-b border-stone-100 dark:border-stone-800/80">
            <p className="text-[10px] font-semibold uppercase tracking-wider text-stone-400 dark:text-stone-500">
              Organizations ({orgs.length})
            </p>
          </div>

          <div className="max-h-60 overflow-y-auto py-1">
            {orgs.map((org) => {
              const isSelected = org.orgId === activeOrgId;
              const role = org.userAssignedRole || (org as any).userRole || (org as any).orgRole || "Member";

              return (
                <button
                  key={org.orgId}
                  onClick={() => {
                    switchOrg(org.orgId);
                    setIsOpen(false);
                  }}
                  className={`w-full flex items-center justify-between px-3 py-2 text-xs text-left transition ${
                    isSelected
                      ? "bg-stone-50 dark:bg-stone-800 font-semibold text-stone-900 dark:text-stone-100"
                      : "text-stone-700 dark:text-stone-300 hover:bg-stone-50/75 dark:hover:bg-stone-800/60"
                  }`}
                >
                  <div className="flex flex-col min-w-0 pr-2">
                    <span className="truncate">{org.orgName}</span>
                    <span className="text-[10px] text-stone-400 dark:text-stone-500 font-normal">{role}</span>
                  </div>
                  {isSelected && <Check className="w-4 h-4 text-stone-900 dark:text-stone-100 shrink-0" />}
                </button>
              );
            })}
          </div>

          {isActiveOrgAdmin && (
            <div className="border-t border-stone-100 dark:border-stone-800/80 px-2 pt-1.5">
              <button
                type="button"
                onClick={() => {
                  setIsOpen(false);
                  redirectToOrgPage();
                }}
                className="w-full inline-flex items-center gap-2 px-2.5 py-1.5 text-xs text-stone-600 dark:text-stone-400 hover:text-stone-900 dark:hover:text-stone-100 hover:bg-stone-100/75 dark:hover:bg-stone-800/60 rounded-lg transition"
              >
                <SettingsIcon className="w-3.5 h-3.5" />
                <span>Organization Settings</span>
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
